import api from "../config/api";
import type { ApiResponse, PaginatedResponse } from "../types";

// Customer Course Types
export interface CustomerCourse {
  _id: string;
  customer: {
    _id: string;
    name: string;
    customerId: string;
    phone: string;
    email?: string;
  };
  course: {
    _id: string;
    name: string;
    category: string | {
      _id: string;
      name: string;
    };
    duration: number;
    pricing: Array<{
      sessions: number;
      price: {
        LAK: number;
        THB: number;
        USD: number;
      };
      validityDays: number;
    }>;
  };
  receipt: {
    _id: string;
    receiptNumber: string;
    total: number;
    currency: string;
    createdAt: string;
  };
  totalSessions: number;
  usedSessions: number;
  remainingSessions: number;
  purchaseDate: string;
  expiryDate: string;
  isActive: boolean;
  usageHistory: Array<{
    _id: string;
    serviceRecord?: {
      _id: string;
      recordId: string;
      startTime: string;
      endTime: string;
      actualDuration: number;
    };
    usedAt: string;
    sessionsUsed: number;
    note?: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerCourseStats {
  usageRate: number;
  remainingRate: number;
  daysUntilExpiry: number;
  isExpired: boolean;
  lastUsed: string | null;
}

export interface ExpiringCoursesData {
  total: number;
  grouped: {
    expiredToday: CustomerCourse[];
    expiring7Days: CustomerCourse[];
    expiring30Days: CustomerCourse[];
  };
  courses: CustomerCourse[];
}

// Request Interfaces
export interface GetCustomerCoursesParams {
  page?: number;
  limit?: number;
  customer?: string;
  course?: string;
  isActive?: boolean;
  isExpired?: boolean;
  search?: string;
}

export interface UseCustomerCourseRequest {
  serviceRecordId?: string;
  sessionsUsed?: number;
}

export interface ExtendCustomerCourseRequest {
  days: number;
  reason?: string;
}

export interface DeactivateCustomerCourseRequest {
  reason?: string;
}

export interface GetExpiringCoursesParams {
  days?: number;
}

// Customer Course Service
export const customerCourseService = {
  // ดึงรายการคอร์สของลูกค้าทั้งหมด
  getCustomerCourses: async (params: GetCustomerCoursesParams = {}): Promise<PaginatedResponse<CustomerCourse>> => {
    const response = await api.get('/customer-courses', { params });
    return response.data;
  },

  // ดึงข้อมูลคอร์สของลูกค้าตาม ID
  getCustomerCourse: async (id: string): Promise<ApiResponse<{ customerCourse: CustomerCourse; stats: CustomerCourseStats }>> => {
    const response = await api.get(`/customer-courses/${id}`);
    return response.data;
  },

  // ใช้คอร์สของลูกค้า (ลดจำนวนครั้ง)
  useCustomerCourse: async (id: string, data: UseCustomerCourseRequest): Promise<ApiResponse<CustomerCourse>> => {
    const response = await api.put(`/customer-courses/${id}/use`, data);
    return response.data;
  },

  // ขยายอายุคอร์สของลูกค้า
  extendCustomerCourse: async (id: string, data: ExtendCustomerCourseRequest): Promise<ApiResponse<CustomerCourse>> => {
    const response = await api.put(`/customer-courses/${id}/extend`, data);
    return response.data;
  },

  // ปิดการใช้งานคอร์สของลูกค้า
  deactivateCustomerCourse: async (id: string, data: DeactivateCustomerCourseRequest): Promise<ApiResponse<CustomerCourse>> => {
    const response = await api.put(`/customer-courses/${id}/deactivate`, data);
    return response.data;
  },

  // ดูคอร์สที่กำลังจะหมดอายุ
  getExpiringCourses: async (params: GetExpiringCoursesParams = {}): Promise<ApiResponse<ExpiringCoursesData>> => {
    const response = await api.get('/customer-courses/expiring/list', { params });
    return response.data;
  },

  // Helper methods สำหรับใช้งานเฉพาะ

  // ดึงคอร์สของลูกค้าที่ยังใช้งานได้
  getActiveCustomerCourses: async (params: GetCustomerCoursesParams = {}): Promise<PaginatedResponse<CustomerCourse>> => {
    return customerCourseService.getCustomerCourses({ ...params, isActive: true, isExpired: false });
  },

  // ดึงคอร์สของลูกค้าที่หมดอายุแล้ว
  getExpiredCustomerCourses: async (params: GetCustomerCoursesParams = {}): Promise<PaginatedResponse<CustomerCourse>> => {
    return customerCourseService.getCustomerCourses({ ...params, isExpired: true });
  },

  // ดึงคอร์สของลูกค้าที่ไม่ได้ใช้งาน
  getInactiveCustomerCourses: async (params: GetCustomerCoursesParams = {}): Promise<PaginatedResponse<CustomerCourse>> => {
    return customerCourseService.getCustomerCourses({ ...params, isActive: false });
  },

  // ดึงคอร์สตามลูกค้า
  getCoursesByCustomer: async (customerId: string, params: GetCustomerCoursesParams = {}): Promise<PaginatedResponse<CustomerCourse>> => {
    return customerCourseService.getCustomerCourses({ ...params, customer: customerId });
  },

  // ดึงคอร์สตามประเภทคอร์ส
  getCoursesByCourse: async (courseId: string, params: GetCustomerCoursesParams = {}): Promise<PaginatedResponse<CustomerCourse>> => {
    return customerCourseService.getCustomerCourses({ ...params, course: courseId });
  },

  // ค้นหาคอร์สของลูกค้า
  searchCustomerCourses: async (search: string, params: GetCustomerCoursesParams = {}): Promise<PaginatedResponse<CustomerCourse>> => {
    return customerCourseService.getCustomerCourses({ ...params, search });
  },

  // ใช้คอร์สแบบง่าย (1 เซสชั่น)
  useOneSession: async (id: string, serviceRecordId?: string): Promise<ApiResponse<CustomerCourse>> => {
    return customerCourseService.useCustomerCourse(id, { serviceRecordId, sessionsUsed: 1 });
  },

  // ขยายอายุคอร์สแบบง่าย (30 วัน)
  extendCourse30Days: async (id: string, reason?: string): Promise<ApiResponse<CustomerCourse>> => {
    return customerCourseService.extendCustomerCourse(id, { days: 30, reason });
  },

  // ปิดการใช้งานคอร์สแบบง่าย
  deactivateCourse: async (id: string, reason?: string): Promise<ApiResponse<CustomerCourse>> => {
    return customerCourseService.deactivateCustomerCourse(id, { reason });
  }
};